import { FC } from 'react'

import { AlignBottom16, AlignLeft16, AlignRight16, AlignTop16 } from '@makyoapp/makyo-icon/icon'
import { Field, SelectDropdown, SingleIconSelect, Slider } from '@makyoapp/makyo-ui'

import { SectionBackgroundGroupProps, SectionColorVariant } from '@/shared/builder/default/section.default'
import { BrandKitColorsDropdown } from '@/components/BrandKitColorsDropdown'

interface ColorBackgroundSelectProps {
  layer: SectionBackgroundGroupProps
  handleUpdate: (id: number, newValues: any) => Promise<void>
  settingId: string
  mode: 'light' | 'dark'
}

export const ColorBackgroundSelect: FC<ColorBackgroundSelectProps> = ({ handleUpdate, layer, settingId, mode }) => {
  const isDark = mode === 'dark'

  const getColorValue = (colorKey: SectionColorVariant | undefined): string => {
    if (!colorKey) return ''
    if (typeof colorKey === 'object' && mode in colorKey) {
      return colorKey[mode] as string
    }
    return colorKey as unknown as string
  }

  const getModeValue = (modeKey: SectionColorVariant | string | undefined): string => {
    if (!modeKey) return 'No Color'
    if (typeof modeKey === 'object' && mode in modeKey) {
      return modeKey[mode] as string
    }
    return modeKey as string
  }
  const handleColorUpdate = (value: string | number | number[], key: string) => {
    const currentValue = (layer.values as any)[key]
    const newValue = {
      light: !isDark
        ? value
        : typeof currentValue === 'object'
          ? (currentValue as SectionColorVariant).light
          : currentValue,
      dark: isDark
        ? value
        : typeof currentValue === 'object'
          ? (currentValue as SectionColorVariant).dark
          : currentValue,
    }
    handleUpdate(layer.id, { [key]: newValue })
  }

  const handleModeUpdate = (value: string) => {
    const currentValue = layer.values.mode
    const newValue = {
      light: !isDark
        ? value
        : typeof currentValue === 'object'
          ? (currentValue as SectionColorVariant).light
          : currentValue,
      dark: isDark
        ? value
        : typeof currentValue === 'object'
          ? (currentValue as SectionColorVariant).dark
          : currentValue,
    }
    handleUpdate(layer.id, { mode: newValue })
  }

  return (
    <div className='space-y-4'>
      <SelectDropdown
        value={getModeValue(layer.values.mode as SectionColorVariant | string | undefined)}
        onChange={(e) => handleModeUpdate(e)}
        options={['No Color', 'Solid Color', 'Gradient']}
      />
      {getModeValue(layer.values.mode as SectionColorVariant | string | undefined) === 'Gradient' && (
        <div className='space-y-2'>
          <BrandKitColorsDropdown
            id={settingId}
            value={getColorValue(layer.values.gradientStart as SectionColorVariant | undefined)}
            onChange={(e) => handleColorUpdate(e, 'gradientStart')}
            showExtendedColors
          />
          <Field isVertical>
            <span className='text-left'>Transparent</span>
            <Slider
              min={0}
              max={100}
              step={1}
              values={[
                (typeof layer.values.gradientStartOpacity === 'object'
                  ? ((layer.values.gradientStartOpacity as SectionColorVariant)[mode] as number)
                  : layer.values.gradientStartOpacity) ?? 10,
              ]}
              onChange={(e) => handleColorUpdate(e, 'gradientStartOpacity')}
              showLabel
            />
          </Field>
          <BrandKitColorsDropdown
            id={settingId}
            value={getColorValue(layer.values.gradientEnd as SectionColorVariant | undefined)}
            onChange={(e) => handleColorUpdate(e, 'gradientEnd')}
            showExtendedColors
          />
          <Field isVertical>
            <span className='text-left'>Transparent</span>
            <Slider
              min={0}
              max={100}
              step={1}
              values={[
                (typeof layer.values.gradientEndOpacity === 'object'
                  ? ((layer.values.gradientEndOpacity as SectionColorVariant)[mode] as number)
                  : layer.values.gradientEndOpacity) ?? 80,
              ]}
              onChange={(e) => handleColorUpdate(e, 'gradientEndOpacity')}
              showLabel
            />
          </Field>
          <div className='w-full flex justify-start'>
            <SingleIconSelect
              id={settingId}
              onClick={async (opt) => {
                handleUpdate(layer.id, { direction: opt.id })
              }}
              options={[
                {
                  icon: <AlignTop16 />,
                  id: 'to top',
                  title: 'To top',
                },
                {
                  icon: <AlignRight16 />,
                  id: 'to right',
                  title: 'To right',
                },
                {
                  icon: <AlignBottom16 />,
                  id: 'to bottom',
                  title: 'To bottom',
                },
                {
                  icon: <AlignLeft16 />,
                  id: 'to left',
                  title: 'To left',
                },
              ]}
              selected={
                ['to top', 'to right', 'to bottom', 'to left'].includes(layer.values.direction as string)
                  ? (layer.values.direction as string)
                  : 'to bottom'
              }
            />
          </div>
        </div>
      )}

      {getModeValue(layer.values.mode as SectionColorVariant | string | undefined) === 'Solid Color' && (
        <>
          <BrandKitColorsDropdown
            id={settingId}
            value={getColorValue(layer.values.color as SectionColorVariant | undefined)}
            onChange={(e) => handleColorUpdate(e, 'color')}
            showExtendedColors
          />
          <Field isVertical>
            <span className='text-left'>Opacity</span>
            <Slider
              min={0}
              max={100}
              step={1}
              values={
                typeof layer.values.colorOpacity === 'object'
                  ? [((layer.values.colorOpacity as SectionColorVariant)?.[mode] as number) ?? 10]
                  : [10]
              }
              onChange={(e) => handleColorUpdate(e?.[0], 'colorOpacity')}
              showLabel
            />
          </Field>
        </>
      )}
    </div>
  )
}
